import { FaGamepad, FaShieldAlt, FaShareAlt } from "react-icons/fa";
import { BsBackpack4 } from "react-icons/bs";

/**
 * Play Page Configuration
 * Centralized configuration for all Play page features
 */

// Enum for special quick link actions
export enum QuickLinkAction {
    OPEN_REWARDS_DRAWER = "openRewardsDrawer",
    OPEN_INVITE_MODAL = "openInviteModal",
}

// Tab configuration
export const PLAY_PAGE_TABS = [
    { id: "games", label: "Games", icon: FaGamepad, enabled: true },
    { id: "share", label: "Share & Play", icon: FaShareAlt, enabled: true },
    /*
     * TODO: Implement these tabs
     * { id: 'guilds', label: 'Guilds', icon: FaShieldAlt, enabled: true },
     * { id: 'inventory', label: 'My Stash', icon: BsBackpack4, enabled: true },
     */
] as const;

// Quick links configuration - can be dynamically loaded from API later
export const QUICK_LINKS_CONFIG = [
    { id: "eat", label: "Eat", icon: "/pngs/Eat-Icon.png", path: "/restaurants", enabled: true },
    { id: "shop", label: "LootShop", icon: "/pngs/lootshop-icon.png", path: import.meta.env.VITE_LOOTSHOP_RESTAURANT_ID || "/restaurants/674a30b9b53b4c9f4aee6587", enabled: true },
    { id: "lootboxes", label: "LootBoxes", icon: "/pngs/rewards-icon.png", path: "/devourlootbox", enabled: true },
    { id: "invite", label: "Invite", icon: "/pngs/invite-icon.png", path: "#", action: QuickLinkAction.OPEN_INVITE_MODAL, enabled: true },
    { id: "rewards", label: "Rewards", icon: "/pngs/rewards-icon.png", path: "#", action: QuickLinkAction.OPEN_REWARDS_DRAWER, enabled: true },
    { id: "account", label: "Account", icon: "/pngs/account-icon.png", path: "/account", enabled: true },
];

// Feature flags for progressive rollout
export const PLAY_PAGE_FEATURES = {
    brandChallenges: {
        enabled: true,
        maxItems: 3,
        autoRotate: true,
        rotationInterval: 5000, // 5 seconds
    },
    quickLinks: {
        enabled: true,
        customizable: false, // Future feature
        maxItems: 6,
    },
    communityFeatures: {
        discord: true,
        lootboxes: true, // Match the id in COMMUNITY_FEATURES
        spin: false, // Match the id in COMMUNITY_FEATURES
    },
    tabs: {
        persistSelection: true,
        deepLinking: true, // URL-based navigation
    },
    performance: {
        lazyLoadTabs: true,
        virtualizeQuests: true,
        skeletonLoaders: true,
    },
};

// Layout configuration
export const PLAY_PAGE_LAYOUT = {
    gridColumns: {
        desktop: "7fr 3fr",
        tablet: "1fr",
        mobile: "1fr",
    },
    spacing: {
        xs: "0.5rem",
        sm: "1rem",
        md: "1.5rem",
        lg: "2rem",
        xl: "3rem",
    },
    breakpoints: {
        mobile: 768,
        tablet: 1200,
        desktop: 1400,
    },
};

// Placeholder data for development
export const PLACEHOLDER_CHALLENGES = [
    {
        id: "snapple-challenge",
        name: "Snapple Challenge",
        cardTitle: "Snapple Fantastic Four Challenge",
        description: "Complete quests to earn exclusive Snapple rewards and level up your gaming experience.",
        brandLogo: "/snapple-logo.png",
        brandColor: "#5865F2",
        brandMapSlug: "snapple",
        backgroundImageLarge: "/snapple-challenge.jpg",
        backgroundImageSmall: "/snapple-challenge.jpg",
        start: new Date("2024-12-01").getTime(),
        end: new Date("2024-12-31").getTime(),
        isAccepted: false,
    },
    {
        id: "monster-challenge",
        name: "Monster Energy Challenge",
        cardTitle: "Monster Energy Gaming Marathon",
        description: "Play longer, earn more. Get Monster Energy rewards for your gaming sessions.",
        brandLogo: "/monster-logo.png",
        brandColor: "#00ff00",
        brandMapSlug: "monster",
        backgroundImageLarge: "/monster-challenge.jpg",
        backgroundImageSmall: "/monster-challenge.jpg",
        start: new Date("2024-12-15").getTime(),
        end: new Date("2025-01-15").getTime(),
        isAccepted: true,
    },
    {
        id: "razer-challenge",
        name: "Razer Challenge",
        cardTitle: "Razer Elite Gamer Challenge",
        description: "Show your skills and win Razer gaming gear in this exclusive challenge.",
        brandLogo: "/razer-logo.png",
        brandColor: "#00ff00",
        brandMapSlug: "razer",
        backgroundImageLarge: "/razer-challenge.jpg",
        backgroundImageSmall: "/razer-challenge.jpg",
        start: new Date("2025-01-01").getTime(),
        end: new Date("2025-01-31").getTime(),
        isAccepted: false,
    },
];

// Community features configuration
export const COMMUNITY_FEATURES = [
    {
        id: "discord",
        title: "Join Us On Discord",
        subtitle: "Join the squad",
        icon: "/images/devourplay-icon.png",
        path: "https://discord.gg/devourplay",
        external: true,
    },
    {
        id: "lootboxes",
        title: "Devour LootBoxes",
        subtitle: "Unlock epic rewards—every box delivers.",
        icon: "/pngs/devour-commonlootbox.png",
        path: "/devourlootbox",
        external: false,
    },
    {
        id: "spin",
        title: "Spin & Earn Wheel",
        subtitle: "Spin the wheel daily!",
        icon: "/wheel-icon.png",
        path: "/spin",
        external: false,
    },
];